import React, { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { useProductReadContext } from '@context/ProductContext'

import { Body1, Caption1 } from '@ui/Typography/Typography'
import { Badge } from '@ui/components/Badge/Badge'

import { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { useFormatNumber } from '@hooks/useFormatNumber'

import PSPLogo from '@assets/images/online-banking-icon-48x48px.png'

import { Box } from '@shared/components/Box'

const SC_TRANSACT365_MYR_ID = 'b0f704f5-628a-11ee-96cf-06bf40e8b50a'
const SC_TRANSACT365_BDT = '9e064c72-7e03-11ee-96cf-06bf40e8b50a'
const SINGAPORE_ONLINE_BANK_TRANSFER_METHOD_ID = 'b82dc307-9d79-11ed-8b0c-0a51dcb142a4'

interface PaymentProviderItemProps {
  item: WalletPaymentProvider
  onSelectOption(option: WalletPaymentProvider): void
}

export const PaymentProviderWithdrawalModalOption: React.FC<PaymentProviderItemProps> = (props) => {
  const { item } = props

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const { isDefaultCFDProductType } = useProductReadContext()

  return (
    <PaymentProviderModalOption {...props}>
      <Box flex direction='column'>
        <Body1 medium>{item.description}</Body1>
        <Caption1 color='text-low-em'>
          {t('Processing Time')} {item.fundingTime}
        </Caption1>
        {isDefaultCFDProductType() && (
          <>
            <Caption1 color='text-low-em'>
              {t('Fee')} {formatMoney(item.fee, item.currency.id)}
            </Caption1>
            <Caption1 color='text-low-em'>
              {t('Minimum')} {t('withdrawal')} {formatMoney(item.minAmount, item.currency.id)}
            </Caption1>
          </>
        )}
      </Box>
    </PaymentProviderModalOption>
  )
}

export const PaymentProviderDepositModalOption: React.FC<PaymentProviderItemProps> = (props) => {
  const { item } = props

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const { isDefaultCFDProductType } = useProductReadContext()
  const showLastUsedBadge = isDefaultCFDProductType() && item.lastDeposit === true

  return (
    <PaymentProviderModalOption {...props}>
      <Box flex direction='column'>
        <Box flex align='start' gap='x8' fullWidth justify='space-between'>
          <Body1 medium>{item.description}</Body1>
          {showLastUsedBadge && (
            <Badge size='xs' type='purple' contrast='high'>
              {t('Last used')}
            </Badge>
          )}
        </Box>
        <Caption1 color='text-low-em'>
          {t('Processing Time')} {item.fundingTime}
        </Caption1>
        {isDefaultCFDProductType() && (
          <>
            <Caption1 color='text-low-em'>
              {t('Fee')} {formatMoney(item.fee, item.currency.id)}
            </Caption1>
            <Caption1 color='text-low-em'>
              {t('Minimum')} {t('deposit')} {formatMoney(item.minAmount, item.currency.id)}
            </Caption1>
          </>
        )}
        {item.method.id === SINGAPORE_ONLINE_BANK_TRANSFER_METHOD_ID && (
          <Caption1 color='text-danger-high-em'>
            {t('The service is unavailable from 4 PM to 9 AM')}
          </Caption1>
        )}
        {item.method.id === SC_TRANSACT365_MYR_ID && (
          <Caption1 color='text-danger-high-em'>{t('Service hours')}</Caption1>
        )}
        {item.method.id === SC_TRANSACT365_BDT && (
          <Caption1 color='text-danger-high-em'>{t('Service hours SC_TRANSACT365_BDT')}</Caption1>
        )}
      </Box>
    </PaymentProviderModalOption>
  )
}

type PaymentProviderModalOptionProps = PropsWithChildren<PaymentProviderItemProps> & {
  badge?: React.ReactNode
}

const PaymentProviderModalOption: React.FC<PaymentProviderModalOptionProps> = (props) => {
  const { item, onSelectOption, children, badge } = props

  return (
    <Box flex onClick={() => onSelectOption(item)} fullWidth>
      {badge}
      <Box flex align='center' gap='x8' fullWidth>
        <Box
          flex
          bg='surface-white'
          radius='round'
          p='x8'
          style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}
        >
          <img src={item.logo || PSPLogo} alt='' style={{ objectFit: 'contain' }} />
        </Box>
        <Box flex direction='column' fullWidth>
          {children}
        </Box>
      </Box>
    </Box>
  )
}
